import { tzdate } from '@repo/tzdate';
import { useState } from 'react';
import {
    CompletedPaymentInfo,
    GiftcardPaymentInfo,
    PaymentInfo,
    Purchase,
    CartItem,
    CheckoutInfo,
    AppliedGiftCard,
    BilberryPromoCodeStatus,
    Translations,
    OctoOrder,
    OctoBookingPatchRequest,
    OctoBookingContact,
} from '@repo/types';
import { useAtom } from 'ximple';
import { useAtomReducer } from 'src/hooks/useAtomReducer';
import { showError } from 'src/utils/widget/error-handling';
import { capitalize } from '@repo/common-utils/TextUtils';
import { ApiError } from '@repo/widget-utils/services/utils/ApiError';
import { getBookingClosedItems } from '@repo/widget-utils/cart/cartUtils';
import { validateCheckoutInfoData } from '@repo/widget-utils/checkout-info-helper';
import {
    getCartItemDisplayStartDate,
    getCartItemDisplayTitle,
} from '@repo/widget-utils/display-helper';
import { guardIsValidPaymentGateway } from '@repo/widget-utils/reservation-helper';
import { configurationAtom } from '@repo/widget-utils/widgetsConfiguration';
import { voucherAtom, resetIgnoredValueCards } from 'src/state/voucher/voucherAtom';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import { CheckoutTabType, IdxOpts } from './views/getTabIndex';
import { cartAtom, selectRegularItems } from 'src/state/cart/cartAtom';
import { checkoutInfoAtom, reset } from 'src/state/checkout-info/checkoutInfoAtom';
import { nativeMemberTokenAtom } from 'src/state/native-membership/nativeMemberTokenAtom';
import {
    commitMemberCardCoverage,
    nativeMemberCoverageAtom,
} from 'src/state/native-membership/nativeMemberCoverageAtom';
import {
    bilberryReservationFromCart,
    getConsumerFromCheckoutInfo,
    getMembershipActivityReservationsFromCart,
    getTimeslotsFromCart,
} from '@repo/widget-utils/ProductMapper';
import { createReservation } from '@repo/widget-utils/services/fetchers/product';
import { companyAtom } from 'src/state/company';
import { postMultiReservation } from '@repo/widget-utils/services/fetchers/membership';
import { errorLog } from '@repo/common-utils/Logger';
import { clearCartEvent, clearRegularItemsEvent } from 'src/state/cart/cart.reducer';
import { dispatchWidgetEvent } from 'src/events/eventDispatcher';
import { contactPersonToCustomer } from 'src/events/customer';
import { PriceSummary } from '@repo/widget-utils/price-helper';
import { findUrlQueryParam } from '@repo/common-utils/query-params-helper';
import { useMemberContext } from 'src/widgets/timeslots/timeslots/MemberContext';
import { storePurchase } from 'src/state/payment/purchase.localstorage';
import { useCartCheckoutContext } from './CartCheckoutContext';
import { localeAtom, useLocale } from '@repo/i18n';
import { patchOctoOrder } from '@repo/widget-utils/services/fetchers/octo-api';
import { octoCartAtom } from 'src/state/octo-cart/octoCartAtom';
import { clearOctoCartEvent } from 'src/state/octo-cart/octoCart.reducer';
import { octoOrderAtom, setOctoOrder } from 'src/state/octo-cart/octoOrderAtom';

export function usePayment() {
    const { t } = useLocale();
    const { mutateBookingsForUser, mutateValueCardsForUser } = useMemberContext();
    const [cartItems] = useAtom(cartAtom);
    const [{ appliedPromoCode, ignoredValueCards }, voucherDispatch] = useAtomReducer(voucherAtom);
    const [{ isUsingPaymentPlan }] = useAtom(paymentPlanAtom);
    const [checkoutInfoData, dispatchCheckoutInfo] = useAtomReducer(checkoutInfoAtom);
    const {
        priceSummary,
        appliedGiftcards,
        setActiveTab,
        setPaymentInfo,
        setCompletedPaymentInfo,
        setIsMakingReservation,
        idxOpts: tabIndexOpts,
        showAddressFields,
        octoOrderInCart,
    } = useCartCheckoutContext();

    const [clickedNext, setClickedNext] = useState(false);

    async function handleGoPayClicked(paymentMethod?: 'invoice') {
        setClickedNext(true);

        if (!checkoutInfoData) return;

        const isCheckoutInfoDataValid = validateCheckoutInfoData(
            checkoutInfoData,
            showAddressFields,
            cartItems,
        );

        if (!isCheckoutInfoDataValid) {
            showError(capitalize(t.please_fill_in_all_required_information));
            return;
        }

        const bookingClosedItems = getBookingClosedItems(cartItems);
        if (bookingClosedItems.length > 0) {
            const productNames = bookingClosedItems.map((item) =>
                capitalize(
                    t.booking_is_closed.parsed(
                        `"${getCartItemDisplayTitle(item)} - ${tzdate(
                            getCartItemDisplayStartDate(item),
                        ).format('lll')}"`,
                    ),
                ),
            );
            showError(productNames.join(', '));
            return;
        }

        reserve(
            t,
            cartItems,
            priceSummary,
            checkoutInfoData,
            setActiveTab,
            setPaymentInfo,
            setIsMakingReservation,
            appliedGiftcards,
            appliedPromoCode,
            tabIndexOpts,
            onPaymentCompleted,
            ignoredValueCards.valueCardIdsToIgnore,
            ignoredValueCards.valueCardProductIdsToIgnore,
            ignoredValueCards.valueCardProductTypeIdsToIgnore,
            isUsingPaymentPlan,
            octoOrderInCart,
            paymentMethod,
        );
    }

    function onPaymentCancelled() {
        // TODO: what if the user go back, make some minor changes, and then want to proceed...
        // now we will make a new reservation.

        dispatchWidgetEvent({
            eventType: 'checkoutStep',
            cartItems: cartItems,
            giftCards: appliedGiftcards.map((appliedGiftcard) => ({
                giftcardReference: appliedGiftcard.giftcardStatus.id,
                priceReduction: appliedGiftcard.priceReduction,
            })),
            promoCode: appliedPromoCode,
            checkoutStep: 'Cancel',
        });

        setPaymentInfo(null);
        setActiveTab('contactinfo');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- tabIndexOpts is used in curried function pattern
    function onPaymentCompleted(tabIndexOpts: Record<string, any>) {
        return async function (
            paymentInfo: PaymentInfo | GiftcardPaymentInfo,
            paymentGatewayResponse: any,
        ) {
            const knownPaymentInfoType = paymentInfo as PaymentInfo;
            const purchasedCartItems = cartItems;
            const completedPaymentInfo: CompletedPaymentInfo = {
                paymentInfo: knownPaymentInfoType,
                paymentGatewayResponse,
                purchasedCartItems,
                paymentId: knownPaymentInfoType.payment_id,
                referenceId: knownPaymentInfoType.reference,
                octoOrder: octoOrderInCart,
            };

            if (octoOrderInCart) {
                await octoCartAtom.update(clearOctoCartEvent());
            }

            dispatchWidgetEvent({
                eventType: 'checkoutStep',
                cartItems: purchasedCartItems,
                promoCode: appliedPromoCode,
                giftCards: appliedGiftcards.map((appliedGiftcard) => ({
                    giftcardReference: appliedGiftcard.giftcardStatus.id,
                    priceReduction: appliedGiftcard.priceReduction,
                })),
                checkoutStep: 'Success',
            });

            dispatchWidgetEvent({
                eventType: 'book',
                cartItems: purchasedCartItems,
                promoCode: appliedPromoCode,
                giftCards: appliedGiftcards.map((appliedGiftcard) => ({
                    giftcardReference: appliedGiftcard.giftcardStatus.id,
                    priceReduction: appliedGiftcard.priceReduction,
                })),
                reference:
                    knownPaymentInfoType.booking?.id.toString() ?? knownPaymentInfoType.reference,
                customer: contactPersonToCustomer(checkoutInfoData?.contactPerson),
            });

            const bookableCartItems = selectRegularItems(cartItems);
            if (bookableCartItems.length > 0) {
                await cartAtom.update(clearRegularItemsEvent());
            } else {
                await cartAtom.update(clearCartEvent());
            }

            dispatchCheckoutInfo(reset());
            setPaymentInfo(null);
            setCompletedPaymentInfo(completedPaymentInfo);
            setActiveTab('payment');
            storePurchase(null);
            voucherDispatch(resetIgnoredValueCards());
            mutateBookingsForUser();
            mutateValueCardsForUser();

            const bbRebookReturnUrl = findUrlQueryParam('bbRebookReturnUrl');

            if (bbRebookReturnUrl) {
                window.location.href =
                    bbRebookReturnUrl + '?newInvoiceId=' + completedPaymentInfo.referenceId;
            }
        };
    }

    return {
        handleGoPayClicked,
        onPaymentCancelled,
        onPaymentCompleted,
        clickedNext,
        setClickedNext,
    };
}

async function reserve(
    t: Translations,
    cartItems: CartItem[],
    priceSummary: PriceSummary,
    checkoutInfoData: CheckoutInfo,
    setActiveTab: (arg: CheckoutTabType) => void,
    setPaymentInfo: (paymentInfo: PaymentInfo | null) => void,
    setIsMakingReservation: (isMakingReservation: boolean) => void,
    appliedGiftcards: AppliedGiftCard[],
    appliedPromoCode: BilberryPromoCodeStatus | null,
    tabIndexOpts: IdxOpts,
    onPaymentCompleted: (
        tabIndexOptions: IdxOpts,
    ) => (paymentInfo: PaymentInfo | GiftcardPaymentInfo, paymentGatewayResponse: any) => void,
    valueCardIdsToIgnore: number[],
    valueCardProductIdsToIgnore: number[],
    valueCardProductTypeIdsToIgnore: number[],
    isUsingPaymentPlan: boolean,
    octoOrder?: OctoOrder,
    paymentMethod?: 'invoice',
) {
    setIsMakingReservation(true);

    const appliedGiftcardIds = appliedGiftcards.map((x) => x.giftcardStatus.id);

    try {
        const paymentInfo = octoOrder
            ? await createReservationThroughOcto(octoOrder, checkoutInfoData)
            : await createReservationThroughBilberryBaseOrMembership(
                  isUsingPaymentPlan,
                  priceSummary,
                  cartItems,
                  checkoutInfoData,
                  appliedGiftcardIds,
                  appliedPromoCode,
                  valueCardIdsToIgnore,
                  valueCardProductIdsToIgnore,
                  valueCardProductTypeIdsToIgnore,
                  paymentMethod,
              );

        // Bilberry backoffice specific behavior
        if (configurationAtom.subject.value.backoffice_skipPayment) {
            onPaymentCompleted(tabIndexOpts)(paymentInfo, null);
            return;
        }

        if (
            (paymentMethod === 'invoice' && paymentInfo.payment_gateway === 'invoice') ||
            (paymentInfo.payment_gateway !== 'adyen' &&
                !paymentInfo.payment_id &&
                paymentInfo.reference &&
                !paymentInfo.booking?.stripe_key) ||
            (paymentInfo.booking?.paid_at && !paymentInfo.payment_id && !paymentInfo.checkout_key)
        ) {
            // Due to applied giftcard or invoice payment
            // Go straight to receipt
            //
            onPaymentCompleted(tabIndexOpts)(paymentInfo, null);
        } else {
            // Proceed to the NETS payment
            setPaymentInfo(paymentInfo);
            setActiveTab('payment');
            dispatchWidgetEvent({
                eventType: 'paymentInfoAdded',
                cartItems: cartItems,
                giftCards: appliedGiftcards.map((appliedGiftcard) => ({
                    giftcardReference: appliedGiftcard.giftcardStatus.id,
                    priceReduction: appliedGiftcard.priceReduction,
                })),
                promoCode: appliedPromoCode,
            });
        }
    } catch (error) {
        errorLog('error: ', error);

        if (error instanceof ApiError) {
            showError(error.message);
            return;
        }

        showError(capitalize(t.error_occurred_when_creating_reservation));
    } finally {
        setIsMakingReservation(false);
    }
}

function getOctoContact(checkoutInfoData: CheckoutInfo, locale: string): OctoBookingContact {
    return {
        firstName: checkoutInfoData.contactPerson.firstName,
        lastName: checkoutInfoData.contactPerson.lastName,
        emailAddress: checkoutInfoData.contactPerson.email,
        phoneNumber: `${checkoutInfoData.contactPerson.phone.dialCode}${checkoutInfoData.contactPerson.phone.number}`,
        locales: [locale],
        country: checkoutInfoData.contactPerson.country?.toString() ?? '',
    };
}

async function createReservationThroughOcto(
    octoOrder: OctoOrder,
    checkoutInfoData: CheckoutInfo,
): Promise<PaymentInfo> {
    const { locale } = localeAtom.subject.value;

    const request: OctoBookingPatchRequest = {
        returnUrl: window.location.origin + window.location.pathname,
        contact: getOctoContact(checkoutInfoData, locale),
    };

    const updatedOrder = await patchOctoOrder(octoOrder.id.toString(), request);

    await octoOrderAtom.update(setOctoOrder(updatedOrder));

    const { cardPayment } = updatedOrder;

    if (!cardPayment) {
        throw new Error('No card payment configuration on order');
    }

    if (cardPayment.gateway === 'adyen') {
        const { adyen } = cardPayment;
        if (!adyen.session) {
            throw new Error('Adyen session not available after order patch');
        }
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_gateway: 'adyen',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_id: null,
            reference: updatedOrder.reference ?? updatedOrder.id,
            adyen: {
                environment: adyen.environment,
                clientKey: adyen.clientKey,
                sessionId: adyen.session.id,
                sessionData: adyen.session.sessionData,
            },
        };
    }

    if (cardPayment.gateway === 'nets') {
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_gateway: 'nets',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            payment_id: cardPayment.nets.paymentId,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            checkout_key: cardPayment.nets.checkoutKey,
            reference: updatedOrder.reference ?? updatedOrder.id,
        };
    }

    throw new Error(`Unsupported payment gateway: ${(cardPayment as { gateway: string }).gateway}`);
}

async function createReservationThroughBilberryBaseOrMembership(
    isUsingPaymentPlan: boolean,
    priceSummary: PriceSummary,
    cartItems: CartItem[],

    checkoutInfoData: CheckoutInfo,
    appliedGiftcardIds: string[],
    appliedPromoCode: BilberryPromoCodeStatus | null,

    valueCardIdsToIgnore: number[],
    valueCardProductIdsToIgnore: number[],
    valueCardProductTypeIdsToIgnore: number[],

    paymentMethod?: 'invoice',
) {
    const bookableCartItems = selectRegularItems(cartItems);

    const purchase: Purchase = {
        referenceId: '',
        paymentId: '',
        amount: 0,
        cartItems: bookableCartItems,
        giftcards: [],
        isAnalyticsNotified: false,
    };

    if (isUsingPaymentPlan && paymentMethod !== 'invoice') {
        const result = await createReservationThroughMembership(
            bookableCartItems,
            checkoutInfoData,
            appliedGiftcardIds,
            appliedPromoCode,
            valueCardIdsToIgnore,
            valueCardProductIdsToIgnore,
            valueCardProductTypeIdsToIgnore,
        );

        purchase.referenceId = result.paymentInfo.reference ?? '';
        purchase.paymentId = result.paymentInfo.payment_id ?? '';
        purchase.amount = priceSummary.totalPrice;

        storePurchase(purchase);

        return result.paymentInfo;
    } else {
        const result = await createReservationThroughBilberryBase(
            bookableCartItems,
            checkoutInfoData,
            appliedGiftcardIds,
            appliedPromoCode,
            paymentMethod,
        );

        purchase.referenceId = result.paymentInfo.reference ?? '';
        purchase.paymentId = result.paymentInfo.payment_id ?? '';
        purchase.amount = result.purchaseAmount;

        storePurchase(purchase);

        return result.paymentInfo;
    }
}

async function createReservationThroughMembership(
    bookableCartItems: CartItem[],
    checkoutInfoData: CheckoutInfo,
    appliedGiftcardIds: string[],
    appliedPromoCode: BilberryPromoCodeStatus | null,

    valueCardIdsToIgnore: number[],
    valueCardProductIdsToIgnore: number[],
    valueCardProductTypeIdsToIgnore: number[],
) {
    const timeslotsReservation = getTimeslotsFromCart(
        bookableCartItems,
        valueCardIdsToIgnore,
        valueCardProductIdsToIgnore,
        valueCardProductTypeIdsToIgnore,
    );

    const tours = getMembershipActivityReservationsFromCart(
        bookableCartItems,
        checkoutInfoData,
        valueCardIdsToIgnore,
        valueCardProductIdsToIgnore,
        valueCardProductTypeIdsToIgnore,
    );

    const consumer = getConsumerFromCheckoutInfo(checkoutInfoData);

    const booking = await postMultiReservation(
        companyAtom.subject.value.currentSite!.key,
        consumer,
        {
            timeslotReservations: timeslotsReservation?.reservations ?? [],
            activityReservations: tours,
            giftcardReferences: appliedGiftcardIds,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            coupon_code: appliedPromoCode?.coupon_code,
            checkoutUrl: window.location.origin + window.location.pathname,
        },
    );

    const paymentInfo: PaymentInfo = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_gateway: 'nets',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_id: booking?.paymentId ?? null,
        reference: booking?.orderReference ?? null,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        checkout_key: companyAtom.subject.value.currentSite!.netsCheckoutKey,
    };

    return {
        paymentInfo,
    };
}

async function createReservationThroughBilberryBase(
    bookableCartItems: CartItem[],
    checkoutInfoData: CheckoutInfo,
    appliedGiftcardIds: string[],
    appliedPromoCode: BilberryPromoCodeStatus | null,

    paymentMethod?: 'invoice',
) {
    const bbRebookFromInvoiceIds = findUrlQueryParam('bbRebookFromInvoiceIds');

    const rebookFromInvoiceIds = bbRebookFromInvoiceIds
        ? bbRebookFromInvoiceIds.split(',').map((id) => parseInt(id))
        : [];

    // Native membership: when a member is logged in, attach the PAT + their per-line card
    // selection so Base applies coverage. Guests get exactly the previous call (no extra args).
    const memberToken = nativeMemberTokenAtom.subject.value;
    const isNativeMember =
        configurationAtom.subject.value.membershipMode === 'native' && Boolean(memberToken);
    const nativeSelection = isNativeMember
        ? {
              lineOverrides: nativeMemberCoverageAtom.subject.value.lineOverrides,
              excludedLineKeys: nativeMemberCoverageAtom.subject.value.excludedLineKeys,
              lineGroups: nativeMemberCoverageAtom.subject.value.lineGroups,
          }
        : undefined;

    const reservation = bilberryReservationFromCart(
        bookableCartItems,
        checkoutInfoData,
        rebookFromInvoiceIds,
        appliedGiftcardIds,
        appliedPromoCode?.coupon_code,
        nativeSelection,
    );
    const booking = await createReservation(
        reservation,
        paymentMethod,
        checkoutInfoData.contactType,
        isNativeMember ? (memberToken ?? undefined) : undefined,
    );
    guardIsValidPaymentGateway(booking);
    // Snapshot the member-card coverage now, while the cart still exists, so the confirmation/receipt
    // can show how the member actually paid (onPaymentCompleted clears the cart + the live coverage).
    if (isNativeMember) commitMemberCardCoverage();

    const paymentInfo: PaymentInfo = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_gateway: booking?.payment_gateway as PaymentInfo['payment_gateway'],
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_id: booking.payment_id,
        reference: booking.id.toString(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        checkout_key: booking.checkout_key ?? undefined,
        reservation,
        booking,
    };

    return {
        paymentInfo,
        purchaseAmount: booking.total,
    };
}
